import type { InvoiceStatusFilter } from "@bezbolisno/contracts/invoices";
import { queryOptions } from "@tanstack/react-query";
import { notFound } from "@tanstack/react-router";
import type { z } from "zod";
import { ApiError, api, expectOk } from "../../lib/api";
import { keys } from "../../lib/keys";

export type InvoiceStatusFilterValue = z.infer<typeof InvoiceStatusFilter>;

export const INVOICE_PAGE_SIZE = 20;

export interface InvoiceListFilters {
  status: InvoiceStatusFilterValue;
  q?: string;
  page: number;
}

export const invoiceListQuery = (filters: InvoiceListFilters) =>
  queryOptions({
    queryKey: keys.invoices({ status: filters.status, q: filters.q ?? "", page: filters.page }),
    queryFn: async () =>
      expectOk(
        await api.invoices.list({
          query: {
            status: filters.status,
            q: filters.q,
            page: filters.page,
            pageSize: INVOICE_PAGE_SIZE,
          },
        }),
        200,
      ),
  });

export const worklistQuery = queryOptions({
  queryKey: keys.invoices({ view: "worklist" }),
  queryFn: async () => expectOk(await api.invoices.worklist({ query: {} }), 200),
});

/** 404 / malformed id → the route's not-found view (the old page called `notFound()`). */
function orNotFound<T>(promise: Promise<T>): Promise<T> {
  return promise.catch((e: unknown) => {
    if (e instanceof ApiError && (e.status === 404 || e.code === "invalid_id")) throw notFound();
    throw e;
  });
}

export const invoiceDraftQuery = (patientId: string) =>
  queryOptions({
    queryKey: keys.invoices({ draft: patientId }),
    queryFn: () =>
      orNotFound(api.invoices.draft({ params: { patientId } }).then((r) => expectOk(r, 200))),
  });

export const invoiceDetailQuery = (invoiceId: string) =>
  queryOptions({
    queryKey: keys.invoice(invoiceId),
    queryFn: () =>
      orNotFound(api.invoices.detail({ params: { invoiceId } }).then((r) => expectOk(r, 200))),
  });
